<template>
  <v-container class="py-8" style="max-width: 860px">
    <h1 class="text-h4 mb-2">Privacy Policy</h1>
    <p class="text-caption grey--text">Effective date: October 21, 2026</p>

    <p>
      This platform provides free online mental health consultations in the Philippines. It is
      operated by the National Center for Mental Health (NCMH) and co-designed with #MentalHealthPH,
      Inc. This policy explains what personal information we collect, how we use it, and your rights
      under the Data Privacy Act of 2012 (Republic Act No. 10173).
    </p>

    <h2 class="text-h6 mt-6">Information we collect</h2>
    <ul>
      <li><strong>Account details:</strong> name, email address, mobile number, password (stored
        hashed), and profile photo.</li>
      <li><strong>Identity verification:</strong> a photo of a government-issued ID (service users)
        or PRC ID (providers).</li>
      <li><strong>Personal background (service users):</strong> birth date, gender, civil status,
        address, educational attainment, religion, affiliation, disability, and emergency
        contacts.</li>
      <li><strong>Health information (service users):</strong> the reason for your consultation, your
        answers to the intake questions, medical history, prescribed medication, and
        hospitalization history. This is sensitive personal information.</li>
      <li><strong>Consultation records:</strong> notes, actions taken, recommendations, and medication
        entered by your provider.</li>
      <li><strong>Appointments:</strong> the date, time, meeting link, and approval status of each
        appointment.</li>
    </ul>

    <h2 class="text-h6 mt-6">How we use it</h2>
    <p>
      We use this information only to verify accounts, schedule and run consultations, let providers
      keep consultation records, and contact you about your appointments. We do not sell your
      information or use it for advertising.
    </p>

    <h2 class="text-h6 mt-6">Who we share it with</h2>
    <ul>
      <li><strong>Your provider and our scheduling staff,</strong> to the extent needed for your
        consultation.</li>
      <li><strong>Google Cloud,</strong> which hosts our application and data.</li>
      <li><strong>Semaphore,</strong> an SMS provider, to send one-time verification codes to your
        mobile number.</li>
      <li><strong>Google Calendar and Google Meet,</strong> to invite you to your consultation (see
        below).</li>
    </ul>

    <h2 class="text-h6 mt-6">Google user data</h2>
    <p>
      Our scheduling staff (secretaries) may connect their own Google account so the platform can
      create Google Meet consultations. When a secretary connects their account, we request
      permission to:
    </p>
    <ul>
      <li><strong>Create Google Calendar events</strong>
        (<code>https://www.googleapis.com/auth/calendar.events</code>). When the secretary approves
        an appointment, we create one event in the secretary's own calendar, with a Google Meet link,
        and invite the provider and the service user by email. We do not read, change, or delete any
        other events in their calendar.</li>
      <li><strong>Manage Google Meet settings</strong>
        (<code>https://www.googleapis.com/auth/meetings.space.settings</code>). We set each meeting we
        create so that the provider and service user can join without someone having to admit them.
        We only change meetings the platform created.</li>
    </ul>
    <p>
      We store only an encrypted access token for the connected account, plus the ID and link of
      each event we create. We do not store the secretary's other calendar data, contacts, or
      email. Google user data is not shared with anyone else, not sold, not used for advertising,
      and not used to train artificial intelligence or machine learning models.
    </p>
    <p>
      Our use of information received from Google APIs adheres to the
      <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank"
        rel="noopener">Google API Services User Data Policy</a>, including the Limited Use
      requirements.
    </p>
    <p>
      A secretary can disconnect at any time from their Google Account at
      <a href="https://myaccount.google.com/permissions" target="_blank"
        rel="noopener">myaccount.google.com/permissions</a>. After that we can no longer create events
      on their behalf. Disconnecting does not remove events already created; they can be deleted
      from Google Calendar.
    </p>

    <h2 class="text-h6 mt-6">How we protect it</h2>
    <p>
      Data is transmitted over encrypted connections and stored on Google Cloud. Passwords are
      hashed, and Google access tokens are encrypted. Access to records is limited by role.
    </p>

    <h2 class="text-h6 mt-6">How long we keep it</h2>
    <!-- TODO(DPO): state the specific retention period and disposal practice,
         especially for consultation notes, which are medical records. -->
    <p>
      We keep personal information only for as long as it is needed for the purposes described
      above, or as required by law, and then dispose of it securely.
    </p>

    <h2 class="text-h6 mt-6">Your rights</h2>
    <p>
      Under the Data Privacy Act you have the right to be informed, to access, to correct, to object,
      to erasure or blocking, to data portability, to damages, and to file a complaint with the
      National Privacy Commission. To exercise these rights, contact us using the details below.
    </p>

    <h2 class="text-h6 mt-6">Contact</h2>
    <!-- TODO(DPO): add the registered Data Protection Officer's name and a
         dedicated email (e.g. a dpo@ role address), and confirm whether NCMH or
         #MentalHealthPH is the personal information controller. -->
    <p>
      National Center for Mental Health<br />
      Nueve de Febrero St., Brgy. Mauway, Mandaluyong City, Metro Manila
    </p>

    <p class="mt-6">
      If you are in crisis, call the DOH-NCMH Crisis Hotline at 1800-1888-1553 (24/7).
    </p>
  </v-container>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
}
</script>
